/**
 * #2211 — the standby activation's refusals, in plain words (never a code on
 * screen). A small module of its own so member-side surfaces that show the
 * covered-by dialog do not pull in the ladder panel.
 */
import { ApiError } from "../../api/client";
import { restDayLabel } from "../../api/useStandbyActivation";

/** `Fri 16 Oct` from the first ISO date in a refusal's tail, if any. */
function dayIn(tail: string): string | null {
  const m = /(\d{4}-\d{2}-\d{2})/.exec(tail);
  return m?.[1] ? restDayLabel(m[1]) : null;
}

/** #2211 — the standby activation's refusals, in plain words. A mapped
 *  code always reads as its own sentence: the text the backend puts after
 *  the code (`already_off_that_day:mc`, an ISO date) is never shown as is —
 *  only a day in it is woven in, formatted (`Fri 16 Oct`). */
const STANDBY_ERRORS: Record<string, { sentence: (tail: string) => string; next?: string }> = {
  call_not_vacant: {
    sentence: () =>
      "Nobody on that call is off sick, so there is no call to swap the standby into.",
    next: "To bring them in as well, use Called in on top.",
  },
  not_on_standby: { sentence: () => "They are not on standby that night." },
  already_on_call: { sentence: () => "They already hold a call that night." },
  not_eligible: { sentence: () => "They are not set up to take that call." },
  standby_absent: { sentence: () => "They are on leave or off sick that day." },
  not_a_call: {
    sentence: () =>
      "That post has no day off after it, so it is not a call a standby is activated onto.",
  },
  standby_changed: {
    sentence: () => "The standby changed while you were looking. Reopen it.",
  },
  already_off_that_day: {
    sentence: (tail) => {
      const why = tail.trim().toLowerCase();
      const off = why === "mc" ? "off sick" : why === "leave" ? "on leave" : "off";
      return `They are already ${off} that day, so there is nothing to give.`;
    },
  },
  nothing_to_release: { sentence: () => "There is nothing to release on that day any more." },
  rest_given_whole_day: {
    sentence: () =>
      "Your team gives the whole next day off after an activation, so there is nothing to keep.",
  },
  rest_date_outside_horizon: {
    sentence: (tail) =>
      `${dayIn(tail) ?? "That day"} is outside this roster's dates — open the roster that has it.`,
  },
  rest_date_in_the_past: {
    sentence: (tail) =>
      `${dayIn(tail) ?? "That day"} has already been worked, so it is not released.`,
  },
  nothing_owed: { sentence: () => "No rest is owed to them for that night." },
  day_not_owed: { sentence: () => "That day is not a rest day they are still owed." },
  call_post_or_on_top: { sentence: () => "Choose the call they take, or Called in on top." },
  kept_date_not_after_call: { sentence: () => "Choose a day after the call." },
  date_and_membership_id_required: { sentence: () => "Something was missing — reopen it." },
};

export function standbyErrorText(detail: string): string | null {
  const match = /^([a-z_]+)(?::\s*(.*))?$/s.exec(detail.trim());
  if (!match) return null;
  const known = STANDBY_ERRORS[match[1] ?? ""];
  if (!known) return null;
  const sentence = known.sentence(match[2] ?? "");
  return known.next ? `${sentence} ${known.next}` : sentence;
}

/** A rest / keep / activation write's error, in words. */
export function restErrorText(err: unknown): string {
  if (!(err instanceof ApiError)) {
    if (err instanceof Error) return err.message;
    return "That could not be saved. Please try again.";
  }
  const detail = err.detail;
  if (detail.startsWith("not_safe:")) {
    const tail = detail.slice("not_safe:".length).trim();
    return tail ? `That day can't be given: ${tail}` : "That day can't be given.";
  }
  return standbyErrorText(detail) ?? detail;
}
